import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { galleriesAPI } from '../../services/api';
import GalleryForm from './GalleryForm';
import '../admin/AdminComponents.css';

const GalleriesManagement = () => {
  const { hasPermission } = useUser();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('veloura');
  const [editingGallery, setEditingGallery] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGalleries();
  }, [selectedBrand]);

  const loadGalleries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await galleriesAPI.getAll(selectedBrand);
      if (response.success) {
        setGalleries(response.data || []);
      } else {
        setError(response.message || 'Failed to load galleries');
      }
    } catch (err) {
      setError(err.message || 'Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (gallery) => {
    try {
      const response = await galleriesAPI.getById(gallery.id);
      if (response.success) {
        setEditingGallery(response.data);
        setShowForm(true);
      } else {
        alert('Failed to load gallery details');
      }
    } catch (error) {
      alert(error.message || 'Failed to load gallery details');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery? This will also delete all images in the gallery.')) {
      try {
        const response = await galleriesAPI.delete(id);
        if (response.success) {
          await loadGalleries();
        } else {
          alert(response.message || 'Failed to delete gallery');
        }
      } catch (error) {
        alert(error.message || 'Failed to delete gallery');
      }
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingGallery(null);
    loadGalleries();
  };

  const handleFormSuccess = () => {
    loadGalleries();
  };

  const handleRemoveImage = async (galleryId, imageUrl) => {
    if (!window.confirm('Are you sure you want to remove this image from the gallery?')) {
      return;
    }

    try {
      const galleryResponse = await galleriesAPI.getById(galleryId);
      if (galleryResponse.success) {
        const imageIndex = galleryResponse.data.images.findIndex(img => img === imageUrl);
        if (imageIndex === -1) {
          alert('Image not found');
          return;
        }

        alert('Image removal requires image IDs. Please use the edit form to manage images.');
      }
    } catch (error) {
      alert(error.message || 'Failed to remove image');
    }
  };

  if (loading && galleries.length === 0) {
    return (
      <div className="admin-section">
        <p>Loading galleries...</p>
      </div>
    );
  }
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Galleries Management</h2>
        <div className="admin-section-actions">
          <select
            className="admin-form-select"
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            style={{ width: 'auto', marginRight: '1rem' }}
          >
            <option value="veloura">Veloura</option>
            <option value="vague">Vague</option>
          </select>
          {galleries.length > 0 && (
            <a
              className="admin-button secondary"
              href={galleriesAPI.getAllDownloadUrl(selectedBrand)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginRight: '1rem', textDecoration: 'none' }}
            >
              Download All ZIP
            </a>
          )}
          {hasPermission('galleries.create') && (
            <button
              className="admin-button primary"
              onClick={() => {
                setEditingGallery(null);
                setShowForm(true);
              }}
            >
              Add Gallery ({selectedBrand})
            </button>
          )}
        </div>

      </div>
      {error && (
        <div className="admin-error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {galleries.length === 0 ? (
        <div className="admin-empty-state">
          <p>No galleries found for {selectedBrand}. Create your first gallery!</p>
        </div>
      ) : (
        <div className="admin-galleries-grid">
          {galleries.map((gallery) => (
            <div key={gallery.id} className="admin-gallery-card">
              <div className="admin-gallery-card-thumbnail">
                <img
                  src={gallery.thumbnail}
                  alt={gallery.title}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="padding: 2rem; text-align: center; color: var(--text-secondary);">No thumbnail</div>';
                  }}
                />
              </div>
              <div className="admin-gallery-card-content">
                <h3>{gallery.title}</h3>
                <p className="admin-gallery-card-meta">
                  {gallery.images?.length || 0} {gallery.images?.length === 1 ? 'image' : 'images'}
                </p>
                <div className="admin-gallery-card-actions">
                  {(gallery.images?.length || 0) > 0 && (
                    <a
                      className="admin-button secondary"
                      href={galleriesAPI.getDownloadUrl(gallery.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      ZIP
                    </a>
                  )}
                  {hasPermission('galleries.edit') && (
                    <button
                      className="admin-button secondary"
                      onClick={() => handleEdit(gallery)}
                    >
                      Edit
                    </button>
                  )}
                  {hasPermission('galleries.delete') && (
                    <button
                      className="admin-button danger"
                      onClick={() => handleDelete(gallery.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

            </div>

          ))}
        </div>

      )}

      {showForm && (
        <GalleryForm
          gallery={editingGallery}
          defaultBrand={selectedBrand}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />

      )}
    </div>

  );
};

export default GalleriesManagement;
